import { GrantType, LoginRequest, LoginResponse, TOKEN_TYPE_HINT } from '@/types//Login'

import cookiesService from '@/services/support/CookiesService'
import api from '@/api'
import qs from 'querystring'
import config from '@/config'
import userInfoStore from '@/store/userInfo'
import { Buffer } from 'buffer'
import { PageResponse } from '@/types/Support'
import { License } from '@/types/catalogs/Licenses'
import LogService from './LogService'

class AuthService {
  // Remove the termination 'rest/' from
  // apiUsersUrl = https://api-{stage}.bluemessaging.net/v1.0/rest/
  APIUsersBaseURL = config.apiUsersUrl.slice(0, -5)
  APIUsersCreateTokensURL = `${this.APIUsersBaseURL}oauth/token`
  APIUsersRevokeTokensURL = `${this.APIUsersBaseURL}oauth/revoke`

  /**
   * Gets a device token from api users
   *
   * @param request with login values
   * @returns response by the api
   */
  async getDeviceToken (request: LoginRequest): Promise<LoginResponse> {
    try {
      const data = {
        username: `${request.user}@${request.domain}`,
        password: request.password,
        uuid: request.uuid,
        device: request.device,
        grant_type: GrantType.PASSWORD_DEVICE,
        source_device: request.sourceDevice
      }
      const config = {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }
      const response = await api.post(this.APIUsersCreateTokensURL, qs.stringify(data), config)
      return response.data
    } catch (error) {
      LogService.error('Error on getting device token. ' + error)
      return Promise.reject(error)
    }
  }
  /**
   * Revokes the token
   *
   * @param token
   */
  async revokeToken (token: string, type: TOKEN_TYPE_HINT): Promise<LoginResponse> {
    try {
      const data = {
        token_type_hint: type,
        token: token
      }
      const config = {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }
      const response = await api.post(this.APIUsersRevokeTokensURL, qs.stringify(data), config)
      return response.data
    } catch (error) {
      LogService.error('Error on revoking token. ' + error)
      return Promise.reject(error)
    }
  }

  async refreshToken (refreshToken: string): Promise<LoginResponse> {
    const requestBody = {
      refresh_token: refreshToken,
      grant_type: 'refresh_token'
    }
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }
    try {
      const response = await api.post(this.APIUsersCreateTokensURL, qs.stringify(requestBody), config)
      this.setAuthCookies(response.data)
      await this.initAuth()
      return response.data
    } catch (error) {
      LogService.error('Error on refresh token. ' + error)
      return Promise.reject(error)
    }
  }

  async getUserInfo () {
    try {
      const response = await api.get(`${config.apiUsersUrl}me`)
      userInfoStore.setUserInfo(response.data)
      return response.data
    } catch (e) {
      LogService.error('Error on - me - request. ' + e)
      return Promise.reject(e)
    }
  }

  /**
   * Gets all the sessions belong to the user.
   *
   * @param credentials values for the basic authentication
   * @returns a page response with the sessions of the user
   */
  async getUserSessions (
    credentials: {
      username: string, // username@domain
      password: string
    }
  ) : Promise<PageResponse<License>> {
    try {
      const endpoint = `${this.APIUsersBaseURL}oauth/sessions`
      const response = await api.get(
        endpoint,
        { auth: credentials }
      )
      return response.data
    } catch (error) {
      LogService.error('Error on getting sesions. ' + error)
      return Promise.reject(error)
    }
  }

  /**
   * Delete a session user by its ID.
   *
   * @param sessionId id of the session to delete
   * @param credentials values for the basic authentication
   * @returns the license deleted
   */
  async deleteUserSession (
    sessionId: number,
    credentials: {
      username: string, // usernama@domain
      password: string
    }
  ) : Promise<License> {
    try {
      const endpoint = `${this.APIUsersBaseURL}oauth/sessions/${sessionId}`
      const response = await api.delete(
        endpoint,
        {
          auth: credentials,
          headers: {
            'Content-Type': 'application/json'
          },
          data: null
        }
      )
      return response.data
    } catch (error) {
      LogService.error('Error on delete session. ' + error)
      return Promise.reject(error)
    }
  }

  async logout (): Promise<void> {
    await api.post('logout')
    api.defaults.headers.common['Authorization'] = ''
  }

  async getDomainName () {
    let token = cookiesService.get(config.jwtAccessTokenCookieName)
    const refreshToken = cookiesService.get(config.refreshTokenCookieName)

    if (!token && refreshToken) {
      await this.refreshToken(refreshToken)
      token = cookiesService.get(config.jwtAccessTokenCookieName)
    }

    const tokenParts = token.split('.')
    const authBase64 = Buffer.from(tokenParts[1], 'base64')
    const authJsonString = authBase64.toString('ascii')
    const authJson = JSON.parse(authJsonString)
    const auth = authJson.sub
    return auth.split('@')[1]
  }

  async getDomainId () {
    let token = cookiesService.get(config.jwtAccessTokenCookieName)
    const refreshToken = cookiesService.get(config.refreshTokenCookieName)

    if (!token && refreshToken) {
      await this.refreshToken(refreshToken)
      token = cookiesService.get(config.jwtAccessTokenCookieName)
    }

    const tokenParts = token.split('.')
    const authBase64 = Buffer.from(tokenParts[1], 'base64')
    const authJsonString = authBase64.toString('ascii')
    const authJson = JSON.parse(authJsonString)
    const ids = authJson.ids
    return ids.split('@')[1]
  }

  async initAuth () {
    try {
      const token = cookiesService.get(config.jwtAccessTokenCookieName)
      if (token && token !== 'undefined') {
        api.defaults.headers.common['Authorization'] = 'Bearer ' + token
        await this.getUserInfo()
      }
    } catch (error) {
      LogService.error('Error initializing user info. ' + error)
      return Promise.reject(error)
    }
  }

  setAuthCookies (auth: LoginResponse) {
    if (auth.access_token && auth.access_token !== 'undefined') {
      cookiesService.set(
        config.jwtAccessTokenCookieName, // name
        auth.access_token, // value
        '12h', // expires / age
        '/', // path
        config.appDomain, // domain
        config.isProduction // secure flag
      )
    }
    if (auth.refresh_token && auth.refresh_token !== 'undefined') {
      cookiesService.set(
        config.refreshTokenCookieName, // name
        auth.refresh_token, // value
        '5d', // expires / age
        '/', // path
        config.appDomain, // domain
        config.isProduction // secure flag
      )
    }
  }
}

export default new AuthService()
